import { useRef } from 'react';
import { CHAT_CONFIG } from '../../config/chatConfig';

const useExponentialBackoff = () => {
  const retryCountRef = useRef(0);
  const currentDelayRef = useRef(CHAT_CONFIG.polling.backoff.initial);

  // Calculate next backoff delay
  const getNextDelay = () => {
    const { initial, multiplier, maxDelay } = CHAT_CONFIG.polling.backoff;
    
    if (retryCountRef.current === 0) {
      currentDelayRef.current = initial;
    } else {
      currentDelayRef.current = Math.min(
        currentDelayRef.current * multiplier,
        maxDelay
      );
    }
    
    return currentDelayRef.current;
  };

  // Reset backoff state on success
  const reset = () => {
    retryCountRef.current = 0;
    currentDelayRef.current = CHAT_CONFIG.polling.backoff.initial;
  };

  // Increment retry count and return whether to continue retrying
  const shouldRetry = () => {
    retryCountRef.current += 1;
    const { maxRetries } = CHAT_CONFIG.polling.backoff;
    return retryCountRef.current < maxRetries;
  };

  // Get current retry information
  const getRetryInfo = () => ({
    attempt: retryCountRef.current,
    maxRetries: CHAT_CONFIG.polling.backoff.maxRetries,
    currentDelay: currentDelayRef.current,
    nextDelay: getNextDelay()
  });

  // Execute retry with exponential backoff
  const executeWithBackoff = async (operation, onError = null) => {
    try {
      const result = await operation();
      reset(); // Reset on success
      return { success: true, data: result };
    } catch (error) {
      const errorMessage = error.message || 'Unknown error';
      
      // Check for network errors - log only in development
      if (process.env.NODE_ENV === 'development') {
        if (errorMessage.includes('Failed to fetch') || errorMessage.includes('ERR_CONNECTION_REFUSED')) {
          console.warn('Backend API is not available. Polling paused.');
        } else {
          console.error('Polling error:', errorMessage);
        }
      }

      if (onError) {
        onError(error);
      }

      if (!shouldRetry()) {
        return { success: false, error: errorMessage, maxRetriesReached: true };
      }

      const delay = getNextDelay();
      const retryInfo = getRetryInfo();
      
      // Return retry information for the calling function to handle the delay
      return { 
        success: false, 
        error: errorMessage, 
        shouldRetry: true,
        retryDelay: delay,
        retryInfo
      };
    }
  };

  return {
    executeWithBackoff,
    reset,
    shouldRetry,
    getRetryInfo,
    getNextDelay
  };
};

export default useExponentialBackoff;